package de.oevbs.kunde.kundenportal2.components

import de.oevbs.kosmos.ui.foundation.BooleanFlowProperty
import de.oevbs.kosmos.ui.icons.Icon
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kosmos.ui.layouts.card
import de.oevbs.kosmos.ui.layouts.disclosure.disclosure
import dev.fritz2.core.*
import dev.fritz2.headless.foundation.*
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLSpanElement


class CollapsibleComponent(id: String? = null) {

    /**
     * Erlaubt den Zugriff auf die ID der Komponente im Initialisierungsblock der Factory-Funktion.
     * Eine explizite Angabe durch den Benutzer ist über die Factory-Funktionen möglich. Die folgenden Prioritäten
     * gelten bei der Vergabe der ID (höchste zu niedrigste Priorität):
     *
     * Explizite ID → Zufällig generierte ID ([Id.next])
     */
    val componentId: String by lazy { id ?: Id.next() }

    /**
     * Der [Effect] dieses Hooks gibt ein [HtmlTag] von [HTMLSpanElement] zurück, so dass man z.B. die Farbe des Icons
     * noch nachträglich bearbeiten kann. Dies wird ggf. für die Schadensübersicht benötigt.
     *
     * Will man noch mehr am linken Header verändern, existiert neben dem kanonischen [invoke] mit separaten
     * Parametern für den Text und die Icons (ungelesen und gelesen) auch eine generische Form, die keinerlei
     * Vorgaben macht und damit maximale Freiheit bietet - aber eben auch keinerlei vorgefertigte Layout und
     * Stylings mitbringt.
     */
    class HeaderLeftHook : Hook<RenderContext, HtmlTag<HTMLSpanElement>, Flow<Boolean>>() {

        operator fun invoke(text: String, icon: Icon, iconRead: Icon) = this.apply {
            this.value = { read, _ ->
                div("flex flex-row gap-2 items-start text-primary-700") {
                    className(read.map { if (it) "vui-label-2" else "vui-label-1" })
                    span {
                        read.map { if (it) iconRead else icon }.render { icon ->
                            icon("w-6 h-6 relative top-0.5", icon = icon)
                        }
                    }.apply { this@HeaderLeftHook.alsoExpr?.let { it() } }
                    span("text-left") { +text }
                }
            }
        }

        operator fun invoke(text: String, icon: Icon) = this(text, icon, icon)

        operator fun invoke(content: RenderContext.(Flow<Boolean>) -> Unit) = this.apply {
            this.value = { read, _ -> content(read) }
        }
    }

    /**
     * Dieser Hook erlaubt die Konfiguration des linken Headerbereichs des Collapsible.
     *
     * Der kanonische Aufruf sieht einen Text und ein davor gestelltes Icon vor. Letzteres muss in zwei Varianten
     * übergeben werden: Die erste für das Icon, welches im ungelesenen Zustand des Content-Bereichs verwendet wird
     * und das Zweite, was dann im gelesenen Zustand angezeigt wird.
     *
     * Es existiert noch ein weiterer [invoke]-Aufruf, der keinerlei Vorgaben macht und der einem erlaubt jeden
     * beliebigen Inhalt zu rendern.
     *
     * @see HeaderLeftHook
     */
    val headerLeft: HeaderLeftHook = HeaderLeftHook()

    data class InfoItem(val text: String, val icon: Icon)

    class InfoItemProperty : Property<List<InfoItem>>() {
        operator fun invoke(text: String, icon: Icon) {
            value = buildList {
                value?.let { addAll(it) }
                add(InfoItem(text, icon))
            }
        }
    }

    /**
     * Diese Property kann beliebig oft verwendet werden, um jeweils ein Paar aus Icon und Text für den rechten
     * Headerberich zu hinterlegen. Das zuerst angegebene Pärchen steht dann im rechten Bereich am weitesten links,
     * also in Leserichtung vorne.
     *
     * ```kotlin
     * headerLeft("12345678", MaterialIconsOutlined.book_2)
     * headerLeft("29.02.2024", MaterialIconsOutlined.calendar_today)
     *
     * // Ausgabe:
     * +-----------------------------------------------------------------------+
     * | linker Bereich                  (icon) 12345678   (icon) 29.02.2024   |
     * +-----------------------------------------------------------------------+
     * ```
     */
    val headerRight: InfoItemProperty = InfoItemProperty()

    /**
     * Über diese Property kann der Inhalt des Collapsibles gerendert werden. Es gibt keinerlei Vorgaben über
     * Formatierung oder Struktur!
     */
    val content: ContentHook = ContentHook()

    /**
     * Über diese Property kann optional ein boolescher [Flow] konfiguriert werden, der die Komponente reaktiv
     * bezüglich des "gelesens"-Zustands macht. Damit wird das Styling des linken Headerbereichs etwa geändert und
     * auch der Rahmen um das ganze Collapsible entfällt bei einem Wert von `true`.
     *
     * Default ist ein [Flow] von `false`
     */
    val isRead: BooleanFlowProperty = BooleanFlowProperty()

    /**
     * Über diese Property kann von außen der Zustand des Öffnens und Schließens gesteuert werden.
     * Die Komponente selber kann diesen natürlich weiterhin verändern, aber mit der Steuerung von außen kann z.B.
     * initial das Collapsible ausgeklappt dargestellt werden oder es kann auch durch eine Aktion von außen
     * automatisch geschlossen werden.
     */
    val openState: DatabindingProperty<Boolean> = DatabindingProperty()

    class HandlerHook : Hook<RenderContext, Unit, Flow<Boolean>>() {
        operator fun invoke(execute: (Boolean) -> Unit) = this.apply {
            this.value = { isOpen, _ ->
                isOpen handledBy { execute(it) }
            }
        }
    }

    /**
     * Über diesen Hook kann man reaktiv auf das Öffnen oder Schließen reagieren.
     * Man kann einfach eine Funktion / Lambda-Expression vom Typ `(Boolean) -> Unit` übergeben, die entsprechend
     * eine Aktion auslöst.
     *
     * Dies kann man nutzen, um z.B. den "gelesen"-Zustand beim angezeigten Objekt zu setzen (was dann wiederum reaktiv
     * die Darstellung der Komponenten beeinflusst)
     *
     * ```kotlin
     * handleToggle { if (it) storedMessage.update(message.copy(read = true)) }
     * ```
     */
    val handleToggle: HandlerHook = HandlerHook()

    fun RenderContext.render(): Tag<HTMLDivElement> = div(id = componentId) {
        card(padding = false) {
            div("px-4 py-1") {
                className(isRead.falseIfAbsent.map { if (it) "" else "border border-primary-500 rounded" })
                disclosure(id = "$componentId-disclosure") {
                    this@CollapsibleComponent.openState.value?.let { openState.use(it) } ?: openState(storeOf(false))
                    hook(handleToggle, openState.data)
                    header {
                        div("w-full mr-12 flex flex-col xl:flex-row gap-8 justify-between") {
                            hook(headerLeft, isRead.falseIfAbsent)
                            div("flex flex-col gap-1 xl:flex-row xl:gap-4") {
                                headerRight.value?.let { items ->
                                    items.forEach { (text, icon) ->
                                        div("flex flex-row gap-2 items-start") {
                                            icon("w-5 h-5 text-secondary-700", icon = icon)
                                            span("vui-label-4 text-left") { +text }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    content {
                        hr("mt-4 border-b border-t-0 border-secondary-200") { }
                        div("p-4") {
                            hook(this@CollapsibleComponent.content)
                        }
                    }
                }
            }
        }
    }

}

/**
 * Diese Factory erzeugt ein Collapsible, was ein Disclosure mit einigen Kundenportal spezifischen Aspekten
 * darstellt. Grundsätzlich beinhaltet es einen immer sichtbaren Kopfbereich und einen Content-Bereich, der durch das
 * Klicken auf den Kopfbereich ein- und ausgeblendet wird.
 *
 * Folgende Eigenschaften und Konfigurationsoptionen bestehen:
 *
 * - Die Komponente ist responsive, so dass die Darstellung des Kopfbereichs bei kleineren Displays vertikal statt
 * horizontal ausgerichtet wird.
 * - die Komponente kennt einen [isRead][CollapsibleComponent.isRead]-Zustand, der im ungelesenen Zustand einen Rahmen
 * um die Komponente legt und auch den linken Kopfbereich beeinflusst.
 * - Man konfiguriert den linken und rechten Kopfbereich getrennt voneinander.
 * - Der linke Bereich besteht in seiner kanonischen Form aus einem [Icon] und einem Text, welcher im ungelesenem
 * Zustand fetter dargestellt wird als im gelesenen
 * - Der rechte Bereich kann beliebig viele Paare aus Icon und Text aufnehmen. Das zuerst konfigurierte Pärchen steht
 * dann in Leserichtung am weitesten vorne.
 * - Die Steuerung des Ein- und Ausklappens kann auch von außen erfolgen.
 * - Man kann auf Änderungen des Ein- und Ausblendens reagieren, indem man in den
 * [handleToggle][CollapsibleComponent.handleToggle]-Hook eine Callback-Funktion hineinreicht.
 *
 * @see CollapsibleComponent.headerLeft
 * @see CollapsibleComponent.headerRight
 * @see CollapsibleComponent.content
 * @see CollapsibleComponent.isRead
 * @see CollapsibleComponent.openState
 * @see CollapsibleComponent.handleToggle
 */
fun RenderContext.collapsible(id: String? = null, initialize: CollapsibleComponent.() -> Unit): Tag<HTMLDivElement> =
    CollapsibleComponent(id).run {
        initialize()
        render()
    }
